<template>
  <main :class="brandClasses?.contactUsMain">
    <vf-notification v-if="errorMessage" class="mb-4" :dismissible="false" type="error" @close="removeErrorNotification">
      {{ errorMessage }}
    </vf-notification>
    <cms-section v-if="eSpots?.['above-dashboard']" :section="eSpots?.['above-dashboard']" />
    <h1 class="mb-6 title-1">
      {{ headerTitle }}
    </h1>
    <client-only>
      <div v-if="showEmployeeContactUs && auth.isEmployee">
        <vf-tile
          v-bind="topTiles.employeeContact"
          :spacing="tileSpacing"
          :style="{ 'max-width': brandStyles?.employeeContactUsTile?.maxWidth }"
        >
          <template #title>
            <span class="ws-pre-line">
              {{ topTiles.employeeContact.title }}
            </span>
          </template>
        </vf-tile>
      </div>
      <div v-else :class="brandClasses.tiles">
        <vf-tile
          v-show="$feature.enableChat"
          v-bind="topTiles.chat"
          data-chat="help:widget"
          :disabled="!isCanada && !$chat.available"
          :spacing="tileSpacing"
          @click="$chat.open({ eventLabel: topTiles.chat.title, buttonLocation: 'contact-us:widget' })"
        />
        <vf-tile v-if="hasFaqPage" v-bind="topTiles.ask" :spacing="tileSpacing" />
        <vf-tile v-if="showEmail" v-bind="topTiles.email" :spacing="tileSpacing" @click="toggleForm" />
        <vf-tile v-if="showPhoneContact" v-bind="topTiles.phone" :spacing="tileSpacing" :to="topTiles.phone.link">
          <template #description>
            <span class="ws-pre-line">
              {{ topTiles.phone.description }}
            </span>
          </template>
        </vf-tile>
      </div>
    </client-only>
    <div
      v-show="showForm"
      id="get-in-touch-with-us"
      :style="{ 'scroll-margin-top': `${header.height.full}px` }"
    >
      <form-contact-us
        ref="formRef"
        :class-title="brandClasses.contactFormTitle"
        @display-error="displayErrorNotification($event as Error)"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
const emit = defineEmits<{
  'change-page-title': [title: string]
}>()

const header = useHeaderStore()

const { data, error } = await useApi().internal.getStaticPage('help/contact-us')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { headerTitle, topTiles } = data.value
const { $seo, $viewport, $chat, $feature, $cookieConsent } = useNuxtApp()
const auth = useAuthStore()
const route = useRoute()

emit('change-page-title', $seo.helpContactUs.title)

const countryCode = useCountryCode()
const isCanada = countryCode === 'CA'
const showEmailContact = !['GR', 'NO'].includes(countryCode)
const showPhoneContact = countryCode !== 'GR'

const eSpots = (await useCms().getSections.contactUs()).data.value?.content.sectionsMap

const {
  brandClasses,
  hasFaqPage,
  showEmployeeContactUs,
  brandStyles,
  tileSpacing,
  launchSierraWithoutTargetingConsent
} = useAppConfig().pages.help

const showForm = ref(false)
const formRef = ref()
const toggleForm = async () => {
  showForm.value = !showForm.value
  await nextTick()
  if (showForm.value) formRef.value?.contactUsFormRef.$el.getElementsByTagName('label')[0].focus()
}

if (route.query.form === 'open' && showEmailContact && !auth.isEmployee) showForm.value = true

const errorMessage = ref('')
const removeErrorNotification = () => errorMessage.value = ''
const displayErrorNotification = (e) => {
  errorMessage.value = e.message
}

const showEmail = computed(
  () => showEmailContact && $feature.autoLaunchAmaChat?.showEmailCard === true
)

onMounted(() => {
  const shouldAutoLaunchAmaChat = computed(() => {
    const helpPageConfig = $feature.autoLaunchAmaChat?.pages.find(({ name }) => name === 'contactUs')

    return $viewport.lg
      ? helpPageConfig?.devices.desktop === true
      : helpPageConfig?.devices.mobile === true
  })

  const hasAutoLaunchedChat = ref(false)

  watchImmediate(
    [
      () => $chat.available?.value,
      () => $chat.amaAvailable?.value,
      () => $cookieConsent?.value?.targeting
    ],
    async ([available, amaAvailable, hasTargetingConsent]) => {
      if (hasAutoLaunchedChat.value || !shouldAutoLaunchAmaChat.value) return

      const shouldLaunchSierra = launchSierraWithoutTargetingConsent && !hasTargetingConsent
      const isSelectedProviderAvailable = shouldLaunchSierra ? available : amaAvailable

      if (!isSelectedProviderAvailable) return

      hasAutoLaunchedChat.value = true
      await nextTick()
      $chat.open()
    }
  )
})
</script>
